import { type FC, useMemo } from "react";

import { useVolumeHoverArea } from "~/feature/chart/hooks/useVolumeHoverArea";
import { useCoreChart } from "~/feature/chart/providers/CoreChartProvider";

import { buildVolumeBars, getBarWidthPercent } from "./volumeBars";
import * as Styled from "./VolumeChart.styles";
import { VolumeChartSkeleton } from "./VolumeChartSkeleton";

const NO_VOLUME_TEXT = "No volume data";

export const VolumeChart: FC = () => {
  const { timeline, volumeByTimeline, isVolumeLoading, settings } =
    useCoreChart();
  const { onMouseMove, onPointerDown, onMouseLeave } = useVolumeHoverArea();

  const bars = useMemo(
    () => buildVolumeBars(volumeByTimeline),
    [volumeByTimeline]
  );

  if (!settings.volume) {
    return null;
  }

  if (isVolumeLoading) {
    return <VolumeChartSkeleton />;
  }

  if (!bars.length) {
    return (
      <Styled.Root>
        <Styled.EmptyState>{NO_VOLUME_TEXT}</Styled.EmptyState>
      </Styled.Root>
    );
  }

  const widthPercent = getBarWidthPercent(timeline.length);

  return (
    <Styled.Root>
      <Styled.ChartArea
        onMouseMove={onMouseMove}
        onPointerDown={onPointerDown}
        onMouseLeave={onMouseLeave}
      >
        <Styled.Bars>
          {bars.map(({ key, leftPercent, heightPercent }) => (
            <Styled.Bar
              key={key}
              $leftPercent={leftPercent}
              $heightPercent={heightPercent}
              $widthPercent={widthPercent}
            />
          ))}
        </Styled.Bars>
      </Styled.ChartArea>
    </Styled.Root>
  );
};
